import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ShiftFilters from './components/ShiftFilters';
import ShiftDataGrid from './components/ShiftDataGrid';
import ShiftDetailPanel from './components/ShiftDetailPanel';
import BulkBookingModal from './components/BulkBookingModal';
import BookingConfirmationModal from './components/BookingConfirmationModal';

const AvailableShiftsBooking = () => {
  // State management
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    departments: [],
    shiftTypes: [],
    minHours: 0,
    maxHours: 12,
    searchQuery: '',
    showOnlyPriority: false,
    showCrossTraining: false
  });

  const [savedFilters, setSavedFilters] = useState([
    { name: 'Meine Morgenschichten', filters: { shiftTypes: ['morning'], departments: ['customer-service'] } },
    { name: 'Wochenend-Schichten', filters: { shiftTypes: ['weekend'] } }
  ]);

  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedShiftForDetails, setSelectedShiftForDetails] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'calendar'
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [shiftToBook, setShiftToBook] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for available shifts
  const [allShifts] = useState([
    {
      id: 'shift-001',
      date: '2025-10-06',
      startTime: '06:00',
      endTime: '14:00',
      duration: 8,
      department: 'customer-service',
      location: 'Hauptgebäude, Etage 2, Raum 201',
      supervisor: 'Maria Schmidt',
      supervisorPhone: '+49 30 12345678',
      requirements: 'Grundkenntnisse in Kundenbetreuung',
      instructions: 'Bitte 15 Minuten vor Schichtbeginn erscheinen. Arbeitskleidung wird gestellt.',
      isPriority: true,
      hasCrossTraining: false,
      status: 'available'
    },
    {
      id: 'shift-002',
      date: '2025-10-06',
      startTime: '14:00',
      endTime: '22:00',
      duration: 8,
      department: 'operations',
      location: 'Lager, Halle A',
      supervisor: 'Thomas Weber',
      supervisorPhone: '+49 30 12345679',
      requirements: 'Gabelstaplerschein erforderlich',
      instructions: 'Sicherheitsschulung am ersten Tag. Schutzausrüstung wird bereitgestellt.',
      isPriority: false,
      hasCrossTraining: true,
      status: 'available'
    },
    {
      id: 'shift-003',
      date: '2025-10-07',
      startTime: '08:00',
      endTime: '16:00',
      duration: 8,
      department: 'manufacturing',
      location: 'Produktionshalle B',
      supervisor: 'Anna Müller',
      supervisorPhone: '+49 30 12345680',
      requirements: 'Erfahrung in der Fertigung von Vorteil',
      instructions: 'Einweisung in Produktionsabläufe erfolgt vor Ort.',
      isPriority: false,
      hasCrossTraining: true,
      status: 'available'
    },
    {
      id: 'shift-004',
      date: '2025-10-07',
      startTime: '22:00',
      endTime: '06:00',
      duration: 8,
      department: 'security',
      location: 'Haupteingang, Pförtnerloge',
      supervisor: 'Michael Fischer',
      supervisorPhone: '+49 30 12345681',
      requirements: 'Sicherheitszertifikat erforderlich',
      instructions: 'Nachtschicht mit besonderen Sicherheitsprotokollen.',
      isPriority: true,
      hasCrossTraining: false,
      status: 'available'
    },
    {
      id: 'shift-005',
      date: '2025-10-08',
      startTime: '09:00',
      endTime: '17:00',
      duration: 8,
      department: 'administration',
      location: 'Verwaltungsgebäude, 3. OG',
      supervisor: 'Sarah Klein',
      supervisorPhone: '+49 30 12345682',
      requirements: 'MS Office Kenntnisse',
      instructions: 'Unterstützung bei administrativen Tätigkeiten.',
      isPriority: false,
      hasCrossTraining: false,
      status: 'available'
    },
    {
      id: 'shift-006',
      date: '2025-10-09',
      startTime: '06:00',
      endTime: '12:00',
      duration: 6,
      department: 'maintenance',
      location: 'Technikraum, Keller',
      supervisor: 'Robert Wagner',
      supervisorPhone: '+49 30 12345683',
      requirements: 'Technische Grundkenntnisse',
      instructions: 'Wartungsarbeiten an Anlagen. Werkzeug wird gestellt.',
      isPriority: false,
      hasCrossTraining: true,
      status: 'available'
    },
    {
      id: 'shift-007',
      date: '2025-10-10',
      startTime: '10:00',
      endTime: '18:00',
      duration: 8,
      department: 'customer-service',
      location: 'Call Center, 1. OG',
      supervisor: 'Lisa Hoffmann',
      supervisorPhone: '+49 30 12345684',
      requirements: 'Fremdsprachenkenntnisse von Vorteil',
      instructions: 'Telefonische Kundenbetreuung. Headset wird bereitgestellt.',
      isPriority: true,
      hasCrossTraining: false,
      status: 'available'
    },
    {
      id: 'shift-008',
      date: '2025-10-11',
      startTime: '12:00',
      endTime: '20:00',
      duration: 8,
      department: 'operations',
      location: 'Versandabteilung',
      supervisor: 'Daniel Richter',
      supervisorPhone: '+49 30 12345685',
      requirements: 'Körperliche Belastbarkeit',
      instructions: 'Kommissionierung und Versand. Einarbeitung erfolgt vor Ort.',
      isPriority: false,
      hasCrossTraining: false,
      status: 'available'
    }
  ]);

  // Filter and sort shifts
  const getFilteredAndSortedShifts = useCallback(() => {
    let filtered = [...allShifts];

    // Apply filters
    if (filters?.dateFrom) {
      filtered = filtered?.filter(shift => shift?.date >= filters?.dateFrom);
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(shift => shift?.date <= filters?.dateTo);
    }
    if (filters?.departments?.length > 0) {
      filtered = filtered?.filter(shift => filters?.departments?.includes(shift?.department));
    }
    if (filters?.shiftTypes?.length > 0) {
      filtered = filtered?.filter(shift => {
        const hour = parseInt(shift?.startTime?.split(':')?.[0]);
        if (filters?.shiftTypes?.includes('morning') && hour >= 6 && hour < 12) return true;
        if (filters?.shiftTypes?.includes('afternoon') && hour >= 12 && hour < 18) return true;
        if (filters?.shiftTypes?.includes('night') && (hour >= 22 || hour < 6)) return true;
        if (filters?.shiftTypes?.includes('weekend')) {
          const dayOfWeek = new Date(shift.date)?.getDay();
          return dayOfWeek === 0 || dayOfWeek === 6;
        }
        if (filters?.shiftTypes?.includes('overtime') && shift?.duration > 8) return true;
        return false;
      });
    }
    if (filters?.minHours > 0) {
      filtered = filtered?.filter(shift => shift?.duration >= filters?.minHours);
    }
    if (filters?.maxHours < 12) {
      filtered = filtered?.filter(shift => shift?.duration <= filters?.maxHours);
    }
    if (filters?.showOnlyPriority) {
      filtered = filtered?.filter(shift => shift?.isPriority);
    }
    if (filters?.showCrossTraining) {
      filtered = filtered?.filter(shift => shift?.hasCrossTraining);
    }
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(shift => 
        shift?.department?.toLowerCase()?.includes(query) ||
        shift?.requirements?.toLowerCase()?.includes(query) ||
        shift?.location?.toLowerCase()?.includes(query)
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [allShifts, filters, sortConfig]);

  const filteredShifts = getFilteredAndSortedShifts();

  // Event handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSaveFilter = (name, filterConfig) => {
    setSavedFilters(prev => [...prev, { name, filters: filterConfig }]);
  };

  const handleLoadFilter = (savedFilter) => {
    setFilters({ ...filters, ...savedFilter?.filters });
  };

  const handleShiftSelect = (shiftIds) => {
    setSelectedShifts(Array.isArray(shiftIds) ? shiftIds : [shiftIds]);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleShiftDetails = (shift) => {
    setSelectedShiftForDetails(shift);
  };

  const handleShiftBook = (shiftId) => {
    const shift = allShifts?.find(s => s?.id === shiftId);
    setShiftToBook(shift);
    setShowConfirmModal(true);
  };

  const handleBulkBook = (shiftIds) => {
    setSelectedShifts(shiftIds);
    setShowBulkModal(true);
  };

  const handleConfirmBooking = async (shiftId) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In real app, this would make an API call to book the shift
    console.log('Booking shift:', shiftId);
    
    setIsLoading(false);
    setShowConfirmModal(false);
    setShiftToBook(null);
    
    // Show success message (in real app, you might want to use a toast notification)
    alert('Schicht erfolgreich gebucht!');
  };

  const handleBulkConfirmBooking = async (shiftIds) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // In real app, this would make an API call to book multiple shifts
    console.log('Booking shifts:', shiftIds);
    
    setIsLoading(false);
    setShowBulkModal(false);
    setSelectedShifts([]);
    
    // Show success message
    alert(`${shiftIds?.length} Schichten erfolgreich gebucht!`);
  };

  // Check for conflicts (mock implementation)
  const getShiftConflicts = (shift) => {
    // In real app, this would check against user's existing bookings
    return [];
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.target?.tagName === 'INPUT' || e?.target?.tagName === 'TEXTAREA') return;
      
      switch (e?.key) {
        case 'j':
          // Navigate down (mock implementation)
          break;
        case 'k':
          // Navigate up (mock implementation)
          break;
        case ' ':
          e?.preventDefault();
          // Toggle selection (mock implementation)
          break;
        case 'Enter':
          // Book selected shift (mock implementation)
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Verfügbare Schichten</h1>
              <p className="text-muted-foreground mt-2">
                Buchen Sie verfügbare Schichten und verwalten Sie Ihre Arbeitszeiten
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                iconName="Grid3X3"
                iconPosition="left"
              >
                Liste
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                iconName="Calendar"
                iconPosition="left"
              >
                Kalender
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
            </div>
          </div>

          {/* Filters */}
          <ShiftFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSaveFilter={handleSaveFilter}
            savedFilters={savedFilters}
            onLoadFilter={handleLoadFilter}
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                {filteredShifts?.length} von {allShifts?.length} Schichten
              </span>
              {selectedShifts?.length > 0 && (
                <span className="text-sm font-medium text-primary">
                  {selectedShifts?.length} ausgewählt
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">Tastaturkürzel:</span>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <kbd className="px-1 py-0.5 bg-muted rounded text-xs">j/k</kbd>
                <span>navigieren</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Space</kbd>
                <span>auswählen</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Enter</kbd>
                <span>buchen</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Shift Data Grid */}
            <div className="lg:col-span-2">
              <ShiftDataGrid
                shifts={filteredShifts}
                selectedShifts={selectedShifts}
                onShiftSelect={handleShiftSelect}
                onShiftBook={handleShiftBook}
                onBulkBook={handleBulkBook}
                sortConfig={sortConfig}
                onSort={handleSort}
                onShiftDetails={handleShiftDetails}
              />
            </div>

            {/* Shift Detail Panel */}
            <div className="lg:col-span-1">
              <ShiftDetailPanel
                shift={selectedShiftForDetails}
                onBook={handleShiftBook}
                onClose={() => setSelectedShiftForDetails(null)}
                conflicts={selectedShiftForDetails ? getShiftConflicts(selectedShiftForDetails) : []}
              />
            </div>
          </div>

          {/* Real-time Status */}
          <div className="mt-8 p-4 bg-card border border-border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-foreground">Live-Updates aktiv</span>
              </div>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Wifi" size={14} />
                  <span>Verbunden</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Database" size={14} />
                  <span>Lohnsystem sync</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span>Letzte Aktualisierung: vor 2 Min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <BulkBookingModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedShifts={selectedShifts}
        shifts={allShifts}
        onConfirmBooking={handleBulkConfirmBooking}
      />
      <BookingConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        shift={shiftToBook}
        onConfirm={handleConfirmBooking}
        isLoading={isLoading}
      />
    </div>
  );
};

export default AvailableShiftsBooking;